import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useLazyQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { Input } from '../../../components/input'
import { Select } from '../../../components/select'
import { Textarea } from '../../../components/textarea'
import { Checkbox, CheckboxField, CheckboxGroup } from '../../../components/checkbox'
import { MultiSelect } from '../../../components/multi-select'
import { GET_ROUTES_FOR_REPORTS } from '../../../graphql/reports/queries'
import { GET_COUNTRY_LIST } from '../../../graphql/common/queries'
import { ErrorDialog, SuccessDialog } from '../../../components/custom/ResponseDialog'
import loadingImage from '../../../assets/images/boat-loading.gif'
import { CREATE_DISCOUNT_PLAN, UPDATE_DISCOUNT_PLAN } from 'graphql/discount/mutations'
import { GET_DISCOUNT_PLAN } from 'graphql/discount/queries'
import { GET_ROUTE_TYPE } from 'graphql/subscription/queries'
import Flatpickr from 'react-flatpickr'

const MAX_FIXED_DISCOUNT_AMOUNT = 9999999

const DiscountsForm = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditMode = !!id

    const [formData, setFormData] = useState({
        influencer_name: { en: '', ar: '' },
        code: '',
        discount: {
            type: 'PERCENTAGE',
            value: '',
        },
        route_type: '',
        route_ids: [],
        trip_ids: [],
        total_count: 0,
        validity: {
            valid_from: '',
            valid_to: '',
        },
        discount_status: '',
        max_uses: 0,
    })

    const [errors, setErrors] = useState({})
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const [routes, setRoutes] = useState([])
    const [countries, setCountries] = useState([])
    const [trips, setTrips] = useState([])
    const [routeTypes, setRouteTypes] = useState([])

    const [getInfluencerDetails, { loading: loadingInfluencer }] = useLazyQuery(GET_DISCOUNT_PLAN, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.getShareAndDiscount?.share_and_discounts?.[0]) {
                const dis = data.getShareAndDiscount.share_and_discounts[0]
                setFormData({
                    influencer_name: dis.influencer_name || { en: '', ar: '' },
                    code: String(dis.code || ''),
                    discount: {
                        type: dis.config.discount.type || '',
                        value: dis.config.discount.value || '',
                    },
                    route_type: dis.route_type || '',
                    route_ids: dis.route_ids || [],
                    trip_ids: dis.trip_ids || [],
                    validity: {
                        valid_from: toInputDate(dis.validity?.valid_from) || '',
                        valid_to: toInputDate(dis.validity?.valid_to) || '',
                    },
                    max_uses: dis.max_uses ? parseInt(dis.max_uses) : null,
                    discount_status: dis.discount_status || 'DRAFT',
                })
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch Influencer details'))
            setShowErrDialog(true)
        },
    })

    const [getRoutes] = useLazyQuery(GET_ROUTES_FOR_REPORTS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.getBoatRoute?.success && data?.getBoatRoute?.boat_routes) {
                setRoutes(data.getBoatRoute.boat_routes)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch routes'))
            setShowErrDialog(true)
        },
    })

    const [getCountries] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.country) {
                setCountries(data.country)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch countries'))
            setShowErrDialog(true)
        },
    })

    const [getRouteTypes] = useLazyQuery(GET_ROUTE_TYPE, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data?.routeType) {
                setRouteTypes(data.routeType)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('Failed to fetch route types'))
            setShowErrDialog(true)
        },
    })

    const influencerStatuses = [
        { value: 'ACTIVE', description: 'Active' },
        { value: 'INACTIVE', description: 'Inactive' },
        { value: 'EXPIRED', description: 'Expired' },
        { value: 'DRAFT', description: 'draft' },
    ]

    const [createInfluencer, { loading: createLoading }] = useMutation(CREATE_DISCOUNT_PLAN, {
        onCompleted: data => {
            if (data.createShareAndDiscount.success) {
                setSuccessMessage(t('Influencer plan created successfully'))
                setShowSucDialog(true)
                setTimeout(() => {
                    navigate('/discounts')
                }, 2000)
            } else {
                setErrorMessage(data.createShareAndDiscount.message || t('Failed to create influencer plan'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('An error occurred while creating influencer plan'))
            setShowErrDialog(true)
        },
    })

    const [updateInfluencer, { loading: updateLoading }] = useMutation(UPDATE_DISCOUNT_PLAN, {
        onCompleted: data => {
            if (data.updateShareAndDiscount.success) {
                setSuccessMessage(t('discounts plan updated successfully'))
                setShowSucDialog(true)
                setTimeout(() => {
                    navigate('/discounts')
                }, 2000)
            } else {
                setErrorMessage(data.updateShareAndDiscount.message || t('Failed to update influencer plan'))
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message || t('An error occurred while updating Influencer plan'))
            setShowErrDialog(true)
        },
    })

    useEffect(() => {
        getCountries({ variables: { id: '' } })
        getRoutes({
            variables: {
                page: { offset: 0, limit: 100 },
                routeId: '',
                routeName: '',
                statusCode: 'ACT',
            },
        })
        getRouteTypes({ variables: { id: '' } })

        // Load Influencer details if in edit mode
        if (isEditMode && id) {
            getInfluencerDetails({
                variables: {
                    filter: { id: id },
                    page: { offset: 0, limit: 1 },
                },
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Populate trips based on selected routes
    useEffect(() => {
        if (formData.route_ids?.length > 0) {
            const selectedRoutes = routes.filter(route => formData.route_ids.includes(String(route.route_id)))
            const allTrips = selectedRoutes.flatMap(route => route.route_trip || [])
            const uniqueTrips = allTrips.filter(
                (trip, index, self) => index === self.findIndex(t => t.trip_id === trip.trip_id)
            )
            setTrips(uniqueTrips)
        } else {
            setTrips([])
        }
    }, [formData.route_ids, routes])

    const toInputDate = isoString => {
        return isoString ? isoString.split('T')[0] : ''
    }

    // Helper function to remove __typename from objects
    const removeTypename = obj => {
        if (obj === null || obj === undefined) return obj
        if (Array.isArray(obj)) return obj.map(removeTypename)
        if (typeof obj === 'object') {
            const { __typename, ...rest } = obj
            return Object.keys(rest).reduce((acc, key) => {
                acc[key] = removeTypename(rest[key])
                return acc
            }, {})
        }
        return obj
    }

    const validateForm = () => {
        const newErrors = {}

        if (!formData.influencer_name.en) newErrors.influencer_name_en = t('Influencer name (English) is required')
        if (!formData.influencer_name.ar) newErrors.influencer_name_ar = t('Influencer name (Arabic) is required')

        const value = Number(formData.discount?.value)
        if (!value || value <= 0) {
            newErrors.value = t('Valid value is required')
        } else if (formData.discount?.type === 'PERCENTAGE' && value > 100) {
            newErrors.value = t('Percentage must be between 1 and 100')
        } else if (formData.discount?.type === 'FIXED_AMOUNT' && value > MAX_FIXED_DISCOUNT_AMOUNT) {
            newErrors.value = t('Fixed amount must be less than or equal to {{max}}', {
                max: MAX_FIXED_DISCOUNT_AMOUNT,
            })
        }

        if (!formData.code) newErrors.code = t('Code is required')

        if (!formData.validity?.valid_from) newErrors.valid_from = t('Valid from date is required')
        if (!formData.validity?.valid_to) newErrors.valid_to = t('Valid to date is required')

        if (formData.valid_from && formData.valid_to && formData.valid_from >= formData.valid_to) {
            newErrors.valid_to = t('Valid to date must be after valid from date')
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = e => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        // Clean the data by removing __typename fields
        const cleanedData = removeTypename(formData)

        const input = {
            influencer_name: cleanedData.influencer_name,
            code: cleanedData.code,
            discount: {
                value: parseFloat(cleanedData.discount.value),
                type: cleanedData.discount.type,
            },
            route_type: cleanedData.route_type,
            route_ids: cleanedData.route_ids,
            trip_ids: cleanedData.trip_ids,
            validity: {
                valid_from: cleanedData.validity.valid_from,
                valid_to: cleanedData.validity.valid_to,
            },
            discount_status: cleanedData.discount_status,
            max_uses: cleanedData.max_uses ? parseInt(cleanedData.max_uses) : null,
        }

        if (isEditMode) {
            updateInfluencer({ variables: { id: id, share_and_discount: input } })
        } else {
            createInfluencer({ variables: { share_and_discount: input } })
        }
    }

    if (loadingInfluencer) {
        return (
            <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                <img alt="logo" src={loadingImage} className="w-[250px]" />
                <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
            </div>
        )
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/discounts')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back to Discounts')}
                </button>
            </div>

            <div className="max-w-4xl mx-auto mb-8">
                <form onSubmit={handleSubmit}>
                    <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
                        <div className="px-4 py-6 sm:p-8">
                            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                                <div className="sm:col-span-6">
                                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                                        {isEditMode ? t('Edit Discount Plan') : t('Create Discount Plan')}
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-gray-600">
                                        {isEditMode
                                            ? t('Update the Discount plan details')
                                            : t('Add a new Discount plan details')}
                                    </p>
                                </div>
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900">{t('Basic Information')}</h3>
                                </div>

                                {/* Basic Information */}
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Influencer Name (English)')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="text"
                                        value={formData.influencer_name.en || ''}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                influencer_name: {
                                                    ...formData.influencer_name,
                                                    en: e.target.value,
                                                },
                                            })
                                        }
                                        required
                                    />
                                    {errors.influencer_name_en && (
                                        <p className="text-red text-xs mt-1">{errors.influencer_name_en}</p>
                                    )}
                                </div>
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Influencer Name (Arabic)')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="text"
                                        value={formData.influencer_name.ar}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                influencer_name: {
                                                    ...formData.influencer_name,
                                                    ar: e.target.value,
                                                },
                                            })
                                        }
                                        required
                                        dir="rtl"
                                    />
                                    {errors.influencer_name_ar && (
                                        <p className="text-red text-xs mt-1">{errors.influencer_name_ar}</p>
                                    )}
                                </div>
                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Promo Code')}
                                    </label>
                                    <Textarea
                                        value={formData.code || ''}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                code: e.target.value,
                                            }))
                                        }
                                        rows={3}
                                    />
                                    {errors.code && <p className="text-red text-xs mt-1">{errors.code}</p>}
                                </div>
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900 mt-4">{t('Pricing & Type')}</h3>
                                </div>
                                {/* { Price and Category} */}
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Amount')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="number"
                                        min="1"
                                        max={
                                            formData.discount?.type === 'FIXED_AMOUNT' ? MAX_FIXED_DISCOUNT_AMOUNT : 100
                                        }
                                        step="1"
                                        value={formData.discount?.value ?? ''}
                                        onKeyDown={e => {
                                            if (
                                                e.key === '-' ||
                                                e.key === 'e' ||
                                                (e.key === '0' && e.currentTarget.value === '')
                                            ) {
                                                e.preventDefault()
                                            }
                                        }}
                                        onChange={e => {
                                            const raw = e.target.value

                                            // allow empty
                                            if (raw === '') {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    discount: {
                                                        ...prev.discount,
                                                        value: '',
                                                    },
                                                }))
                                                return
                                            }

                                            const value = Number(raw)
                                            if (value < 1) return

                                            if (
                                                formData.discount?.type === 'FIXED_AMOUNT' &&
                                                value > MAX_FIXED_DISCOUNT_AMOUNT
                                            ) {
                                                setErrors(prev => ({
                                                    ...prev,
                                                    value: t('Fixed amount must be less than or equal to {{max}}', {
                                                        max: MAX_FIXED_DISCOUNT_AMOUNT,
                                                    }),
                                                }))
                                                return
                                            }

                                            if (formData.discount?.type === 'PERCENTAGE' && value > 100) {
                                                setErrors(prev => ({
                                                    ...prev,
                                                    value: t('Percentage must be between 1 and 100'),
                                                }))
                                                return
                                            }

                                            setFormData(prev => ({
                                                ...prev,
                                                discount: {
                                                    ...prev.discount,
                                                    value,
                                                },
                                            }))
                                            setErrors(prev => ({ ...prev, value: '' }))
                                        }}
                                        required
                                    />

                                    {errors.value && <p className="text-red text-xs mt-1">{errors.value}</p>}
                                </div>

                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Discount Type')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.discount?.type}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                discount: {
                                                    type: e.target.value,
                                                    value: '',
                                                },
                                            }))
                                        }
                                        required
                                    >
                                        <option value="FIXED_AMOUNT">Fixed_Amount</option>
                                        <option value="PERCENTAGE">Percentage</option>
                                    </Select>
                                </div>

                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Max Discount Uses')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Input
                                        type="number"
                                        min="1"
                                        max={MAX_FIXED_DISCOUNT_AMOUNT}
                                        step="1"
                                        value={formData.max_uses ?? ''}
                                        onKeyDown={e => {
                                            if (e.key === '-' || e.key === 'e') {
                                                e.preventDefault()
                                            }
                                        }}
                                        onChange={e => {
                                            const raw = e.target.value

                                            // allow empty
                                            if (raw === '') {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    max_uses: '',
                                                }))
                                                return
                                            }

                                            const value = Number(raw)

                                            if (value < 1) return

                                            if (value > MAX_FIXED_DISCOUNT_AMOUNT) {
                                                setErrors(prev => ({
                                                    ...prev,
                                                    max_uses: t('Maximum uses must be less than or equal to {{max}}', {
                                                        max: MAX_FIXED_DISCOUNT_AMOUNT,
                                                    }),
                                                }))
                                                return
                                            }

                                            setErrors(prev => ({
                                                ...prev,
                                                max_uses: '',
                                            }))

                                            setFormData(prev => ({
                                                ...prev,
                                                max_uses: value,
                                            }))
                                        }}
                                        required
                                    />
                                </div>
                                {/* Validity and Status */}
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900 mt-4">{t('Validity & Status')}</h3>
                                </div>
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Valid From')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Flatpickr
                                        value={formData.validity?.valid_from || null}
                                        placeholder="yyyy-mm-dd"
                                        options={{
                                            dateFormat: 'Y-m-d',
                                            minDate: 'today',
                                        }}
                                        onChange={([date]) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                validity: {
                                                    ...prev.validity,
                                                    valid_from: date.toISOString().split('T')[0],
                                                },
                                            }))
                                        }}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                        required
                                    />
                                    {errors.valid_from && <p className="text-red text-xs mt-1">{errors.valid_from}</p>}
                                </div>

                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Valid To')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Flatpickr
                                        value={formData.validity?.valid_to || null}
                                        placeholder="yyyy-mm-dd"
                                        options={{
                                            dateFormat: 'Y-m-d',
                                            minDate: 'today',
                                        }}
                                        onChange={([date]) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                validity: {
                                                    ...prev.validity,
                                                    valid_to: date.toISOString().split('T')[0],
                                                },
                                            }))
                                        }}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                                        required
                                    />
                                    {errors.valid_to && <p className="text-red text-xs mt-1">{errors.valid_to}</p>}

                                    {errors.valid_to && <p className="text-red text-xs mt-1">{errors.valid_to}</p>}
                                </div>
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Status')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.discount_status}
                                        onChange={e => setFormData({ ...formData, discount_status: e.target.value })}
                                        required
                                    >
                                        <option value="">{t('Select Status')}</option>
                                        {influencerStatuses.map(status => (
                                            <option key={status.value} value={status.value}>
                                                {status.value}
                                            </option>
                                        ))}
                                    </Select>
                                </div>

                                {/* Eligibility */}
                                <div className="sm:col-span-6">
                                    <h3 className="text-lg font-medium text-gray-900 mt-4">{t('Eligibility')}</h3>
                                </div>
                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">
                                        {t('Route Category')}
                                        <span className="text-red">*</span>
                                    </label>
                                    <Select
                                        value={formData.route_type}
                                        onChange={e =>
                                            setFormData({
                                                ...formData,
                                                route_type: e.target.value,
                                                route_ids: [],
                                                trip_ids: [],
                                            })
                                        }
                                        required
                                    >
                                        <option value="">{t('Select Route Category')}</option>
                                        {routeTypes.map(type => (
                                            <option key={type.code} value={type.code}>
                                                {type.description?.en || type.code}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                                        {t('Routes')}
                                    </label>
                                    <MultiSelect
                                        placeholder={t('Select routes')}
                                        value={formData.route_ids || []}
                                        onChange={values =>
                                            setFormData({
                                                ...formData,
                                                route_ids: values,
                                            })
                                        }
                                        options={routes
                                            .filter(
                                                route =>
                                                    route.master_route?.route_type?.code ===
                                                    (formData.route_type === 'DMC' ? 'DMC' : 'INL')
                                            )
                                            .map(route => ({
                                                value: route.route_id,
                                                label: route.route_name?.en || route.route_name,
                                            }))}
                                        disabled={formData.route_type?.length === 0}
                                    />
                                    {formData.route_type?.length === 0 && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            {t('Please select route category first to see available routes')}
                                        </p>
                                    )}
                                </div>

                                <div className="sm:col-span-6">
                                    <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                                        {t('Trips')}
                                    </label>
                                    <MultiSelect
                                        placeholder={t('Select trips')}
                                        value={formData.trip_ids || []}
                                        onChange={values =>
                                            setFormData({
                                                ...formData,
                                                trip_ids: values,
                                            })
                                        }
                                        options={trips.map(trip => ({
                                            value: trip.trip_id,
                                            label: trip.trip_name?.en || trip.trip_name,
                                        }))}
                                        disabled={formData.route_ids?.length === 0}
                                    />
                                    {formData.route_ids?.length === 0 && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            {t('Please select routes first to see available trips')}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
                            <button
                                type="button"
                                onClick={() => navigate('/discounts')}
                                className="text-sm font-semibold leading-6 text-gray-900"
                            >
                                {t('Cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={createLoading || updateLoading}
                                className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50"
                            >
                                {createLoading || updateLoading
                                    ? t('Saving...')
                                    : isEditMode
                                      ? t('Update Discount')
                                      : t('Create Discount')}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
            {showSucDialog && (
                <SuccessDialog
                    status={showSucDialog}
                    title={t('Success')}
                    message={successMessage}
                    onClose={() => {
                        setShowSucDialog(false)
                        setSuccessMessage('')
                    }}
                />
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={showErrDialog}
                    title={t('Error')}
                    message={errorMessage}
                    onClose={() => {
                        setShowErrDialog(false)
                        setErrorMessage('')
                    }}
                />
            )}
        </>
    )
}

export default DiscountsForm
